import config from 'config'
import useScript from 'react-script-hook'
import { useMemo } from 'react'

import { loadLendersRates } from 'common/utils/rates'

import useActiveLenders from './useActiveLenders'

const verticalNameMap = {
  refi: 'refi',
  in_school: 'inschool',
}

/**
 * @param {Object} options - Configuration object
 * @param {string} options.vertical - The vertical to filter lenders by ('refi', 'in_school', 'personal')
 * @param {Array} options.includeLenders - An array of lender slugs to include in the results
 * @param {Function} options.sortCallback - A callback function to sort the lenders
 * @returns
 */
const useActiveLendersWithRates = ({ vertical, includeLenders = [], sortCallback }) => {
  const availableLenders = useActiveLenders(vertical)

  const filteredLenders = useMemo(() => {
    return includeLenders.length && availableLenders.length
      ? includeLenders.map((slug) => {
          const matchedLender = availableLenders.find((lender) => lender.slug === slug)
          // If the lender from includeLenders is not found in availableLenders,
          // return an object with just the slug
          // in order to match the length of includeLenders array
          return matchedLender || { slug, rateSlug: slug }
        })
      : availableLenders
  }, [availableLenders, includeLenders])

  const [scriptLoading, scriptError] = useScript({
    src: config.ratesLibraryUrl,
    charset: 'utf-8',
    type: 'text/javascript',
  })

  const lendersWithRates = useMemo(() => {
    if (!scriptLoading && !scriptError) {
      return loadLendersRates({
        availableLenders: filteredLenders,
        filters: { vertical: verticalNameMap[vertical] },
        sortCallback,
      })
    }
    return []
  }, [scriptLoading, scriptError, filteredLenders, sortCallback])

  return lendersWithRates
}

export default useActiveLendersWithRates
